import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ShieldCheck, AlertTriangle, X, FileWarning, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const PII_KEYWORDS = [
  "student","students","iep","504","discipline","suspension","expulsion",
  "attendance_roster","attendance roster","student_roster","student roster",
  "gradebook","health","counseling","hib","special education","special_education",
  "student_id","studentid","pupil","child_name","child name","assessment_export",
  "first_name","last_name","date_of_birth","dob",
];

export const detectFilenameRisk = (name = "") => {
  const t = (name || "").toLowerCase();
  const hits = [];
  for (const kw of PII_KEYWORDS) {
    const norm = kw.toLowerCase();
    // Word boundary excludes alnum only — _, -, ., whitespace are separators.
    const re = new RegExp(`(?<![a-z0-9])${norm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![a-z0-9])`, "i");
    if (re.test(t)) hits.push(kw);
  }
  return { risky: hits.length > 0, keywords: hits };
};

/**
 * Evidence Attestation Modal — Aggregate Data Safety Phase A.
 *
 * Props:
 *   open: boolean
 *   onCancel(): void
 *   onConfirm(): void   // user attested + agreed to upload (caller proceeds with file pick / upload)
 *   pendingFileName?: string  // optional — if a file is already chosen, show risk warning
 *   onAcknowledgeOnboarding?(): Promise<void>  // record evidence_upload ack on first use
 *   ackComplete?: boolean   // if true skip onboarding step
 */
export default function EvidenceAttestationModal({
  open,
  onCancel,
  onConfirm,
  pendingFileName = "",
  onAcknowledgeOnboarding,
  ackComplete = true,
}) {
  const [agreed, setAgreed] = useState(false);
  const [acknowledgedOnboarding, setAcknowledgedOnboarding] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAgreed(false);
      setAcknowledgedOnboarding(ackComplete);
    }
  }, [open, ackComplete]);

  const fileRisk = useMemo(() => detectFilenameRisk(pendingFileName), [pendingFileName]);

  if (!open) return null;

  const needsOnboardingAck = !ackComplete;

  const handleConfirm = async () => {
    if (!agreed) return;
    if (needsOnboardingAck && !acknowledgedOnboarding) return;
    setSubmitting(true);
    try {
      if (needsOnboardingAck && onAcknowledgeOnboarding) {
        await onAcknowledgeOnboarding();
      }
      onConfirm();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 p-3 sm:p-4"
      data-testid="evidence-attestation-modal"
    >
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div
          className="px-5 py-4 flex items-center justify-between"
          style={{ background: "#0b1220", color: "#fff" }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck className="h-5 w-5 text-[#00698a] shrink-0" />
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#00698a]">
                Aggregate Data Safety
              </div>
              <h2 className="text-[15px] sm:text-[16px] font-bold truncate" data-testid="attestation-title">
                Evidence Upload Attestation Required
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 shrink-0"
            aria-label="Cancel upload"
            data-testid="attestation-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 sm:px-6 py-5 overflow-y-auto text-[#0f172a] flex-1 min-h-0">
          <div
            className="rounded-xl p-4 mb-4"
            style={{ background: "#fef3c7", border: "1px solid #fde68a" }}
          >
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[#92400e]" />
              <div className="text-[12.5px] text-[#92400e] leading-relaxed">
                <strong className="font-bold">Do not upload student-level records or personally identifiable student information.</strong>{" "}
                MySchoolRx is designed for school-level, aggregate, and de-identified information only.
                Do not upload documents containing student names, student IDs, IEP records, health records, discipline records,
                student-level attendance records, student-level assessment records, or any other student-level education records.
              </div>
            </div>
          </div>

          {pendingFileName && (
            <div
              className="rounded-lg border p-3 mb-4 flex items-center gap-2.5"
              style={{
                background: fileRisk.risky ? "#fee2e2" : "#f1f5f9",
                borderColor: fileRisk.risky ? "#fecaca" : "#e2e8f0",
              }}
              data-testid={fileRisk.risky ? "attestation-risk-warning" : "attestation-file-preview"}
            >
              {fileRisk.risky ? (
                <FileWarning className="h-4 w-4 text-[#b91c1c] shrink-0" />
              ) : (
                <CheckCircle2 className="h-4 w-4 text-[#475569] shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold text-[#0f172a] truncate">{pendingFileName}</div>
                {fileRisk.risky && (
                  <div className="mt-1 text-[11.5px] text-[#b91c1c]">
                    This filename contains language ({fileRisk.keywords.slice(0, 3).join(", ")}
                    {fileRisk.keywords.length > 3 ? "…" : ""}) that may indicate student-level information.
                    Please confirm this file has been reviewed and contains no student-level records or personally identifiable student information.
                  </div>
                )}
              </div>
            </div>
          )}

          {needsOnboardingAck && (
            <label
              className="flex items-start gap-2.5 mb-3 cursor-pointer"
              data-testid="attestation-onboarding-ack-label"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-[#cbd5e1] text-[#16a34a]"
                checked={acknowledgedOnboarding}
                onChange={(e) => setAcknowledgedOnboarding(e.target.checked)}
                data-testid="attestation-onboarding-ack-checkbox"
              />
              <span className="text-[12.5px] text-[#0f172a] leading-relaxed">
                I understand that the Diagnostic should be completed using aggregate, school-level, or de-identified information only.
              </span>
            </label>
          )}

          <label
            className="flex items-start gap-2.5 cursor-pointer"
            data-testid="attestation-checkbox-label"
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-[#cbd5e1] text-[#16a34a]"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="attestation-checkbox"
            />
            <span className="text-[12.5px] text-[#0f172a] leading-relaxed font-semibold">
              I confirm this evidence does not contain student-level records or personally identifiable student information.
            </span>
          </label>
        </div>

        {/* Footer */}
        <div
          className="px-5 sm:px-6 py-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2.5"
          style={{ borderTop: "1px solid #e5e7eb" }}
        >
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="w-full sm:w-auto"
            data-testid="attestation-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!agreed || (needsOnboardingAck && !acknowledgedOnboarding) || submitting}
            className="w-full sm:w-auto bg-[#16a34a] hover:bg-[#15803d] text-white disabled:opacity-50"
            data-testid="attestation-confirm-btn"
          >
            {submitting ? "Recording…" : "Confirm & Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Hook: fetch + manage data-safety onboarding acks (per-user)
// ────────────────────────────────────────────────────────────────────────

export const useDataSafetyAcks = () => {
  const [state, setState] = useState({ loading: true, diagnostic_entry: false, evidence_upload: false, ai_use: false });

  const headers = useMemo(() => {
    try {
      const raw = localStorage.getItem("diag_session");
      if (!raw) return {};
      const s = JSON.parse(raw);
      if (!s?.user?.id || !s?.tenant?.id) return {};
      return {
        "x-tenant-id": s.tenant.id,
        "x-user-id": s.user.id,
        "x-user-role": s.user.role || "tenant_admin",
      };
    } catch { return {}; }
  }, []);

  const refresh = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await axios.get(`${API}/api/data-safety/onboarding-status`, { headers });
      setState({ loading: false, ...r.data });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const acknowledge = async (ackType) => {
    try {
      await axios.post(`${API}/api/data-safety/acknowledge`, { ack_type: ackType }, { headers });
      setState((s) => ({ ...s, [ackType]: true }));
    } catch { /* swallow — non-blocking */ }
  };

  return { ...state, refresh, acknowledge };
};
